import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["showOpacityInput", "showInput"],
  _excluded2 = ["color", "colorValidator", "disabled", "inputProps", "onColorChange", "onOpacityChange", "onOpenChange", "open", "readOnly", "showInput", "swatchProps", "opacity", "ColorPicker", "colorInputLabel", "opacityInputLabel", "opacityInputProps", "use", "defaultColor", "defaultOpacity"];
import { useState, useEffect, useRef } from 'react';
import styled from 'foundations-theming/utils';
import { InputStaticLabel } from '../InputStaticLabel';
import { TextInput } from '../TextInput/TextInput';
import { ColorSwatch } from '../ColorSwatch';
import { Controllable } from '../Controllable';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import memoizeOne from 'react-utils/memoizeOne';
import { ColorPickerShim } from './ColorPickerShim';
import { Popover } from '../Popover';
import { FormControl } from '../FormControl/FormControl';
import { View } from '../../../components/View/View';
import { StepperInput } from '../StepperInput';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isValidColorHex = color => /^#?[0-9A-Fa-f]{0,6}$/.test(color);
const DEFAULT_COLOR = '';
const COLOR_INPUT_ALIGNMENT = 'top';
const isEmptyState = color => color === DEFAULT_COLOR || color === null || color === '#';
const prependHexChar = color => color[0] === '#' ? color : `#${color}`;
const getDisplayColor = color => (color || '').replace('#', '');
const getSwatchColor = (color, lastColor = '#ffffff') => {
  if (isEmptyState(color)) return '#ffffff';
  if (color.length > 4 && color.length < 7) {
    return color.slice(0, 4);
  }
  return color.length <= 3 ? lastColor : color;
};
const NoopColorPicker = () => /*#__PURE__*/_jsx("span", {});
const StyledFormControl = styled(props => {
  const {
      showOpacityInput,
      showInput
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(View, {
    display: showInput ? 'inline-block' : 'none',
    inlineSize: showOpacityInput ? '95px' : '115px',
    marginInlineStart: showOpacityInput ? '200' : '0',
    children: /*#__PURE__*/_jsx(FormControl, Object.assign({}, rest))
  });
}).withConfig({
  displayName: "ColorInput__StyledFormControl",
  componentId: "kqnk6k-0"
})([""]);
const SwatchWrapper = styled.div.withConfig({
  displayName: "ColorInput__SwatchWrapper",
  componentId: "kqnk6k-1"
})(({
  theme,
  $showInput
}) => {
  return {
    display: 'inline-block',
    verticalAlign: COLOR_INPUT_ALIGNMENT,
    marginLeft: $showInput ? theme.space[200] : 0
  };
});
const ColorPickerWrapper = styled.div.withConfig({
  displayName: "ColorInput__ColorPickerWrapper",
  componentId: "kqnk6k-2"
})(["box-shadow:none;border:0;"]);
export const ColorInputComponent = _ref => {
  let {
      color = DEFAULT_COLOR,
      colorValidator = isValidColorHex,
      disabled = false,
      inputProps,
      onColorChange,
      onOpacityChange,
      onOpenChange,
      open = false,
      readOnly = false,
      showInput = true,
      swatchProps = {
        size: 20
      },
      opacity,
      ColorPicker = NoopColorPicker,
      colorInputLabel,
      opacityInputLabel,
      opacityInputProps,
      use = 'default'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
  const [prevColor, setPrevColor] = useState(color);
  const [swatchColor, setSwatchColor] = useState(getSwatchColor(color));
  const _triggerEl = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const _renderColorPickerPanel = memoizeOne((computedColorPicker, panelOpen, panelSwatchColor, panelOpacity, handleColorChange, handleOpenChange, popoverTarget) => {
    if (!panelOpen && computedColorPicker !== 'native' || computedColorPicker === NoopColorPicker) return null;
    const renderedContent = CustomRenderer.render(computedColorPicker === 'native' ? ColorPickerShim : computedColorPicker, {
      panelOpen,
      hexOnly: false,
      color: panelSwatchColor,
      alpha: panelOpacity,
      onChange: handleColorChange,
      mode: 'RGB',
      includeAlpha: Boolean(panelOpacity)
    });
    return computedColorPicker === 'native' ? renderedContent : /*#__PURE__*/_jsx(Popover, {
      closeOnOutsideClick: true,
      content: renderedContent,
      onOpenChange: handleOpenChange,
      open: panelOpen,
      placement: "right",
      target: popoverTarget,
      children: /*#__PURE__*/_jsx("span", {})
    });
  });
  useEffect(() => {
    if (color !== prevColor) {
      setPrevColor(color);
      setSwatchColor(getSwatchColor(color, swatchColor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);
  const handleColorChange = evt => {
    if (opacity && evt.alpha && onOpacityChange) {
      onOpacityChange(evt.target ? evt : syntheticEvent(evt.alpha));
    }
    if (onColorChange) {
      onColorChange(evt.target ? evt : syntheticEvent(evt.color));
    }
  };
  const handleInputChange = ({
    target: {
      value
    }
  }) => {
    if (readOnly) return;
    if (colorValidator(value)) {
      if (!value) {
        handleColorChange(syntheticEvent(null));
      } else {
        handleColorChange(syntheticEvent(prependHexChar(value)));
      }
    }
  };
  const handleOpacityInputChange = evt => {
    if (readOnly) return;
    if (onOpacityChange) {
      onOpacityChange(evt);
    }
  };
  const handleOpenChange = () => {
    if (onOpenChange) {
      onOpenChange(syntheticEvent(!open));
    }
  };
  const computedColorPicker = readOnly || disabled ? NoopColorPicker : ColorPicker;
  const renderedHexInput = disabled ? /*#__PURE__*/_jsx(TextInput, Object.assign({
    disabled: true,
    use: use,
    value: `#${getDisplayColor(color)}`
  }, inputProps)) : /*#__PURE__*/_jsx(InputStaticLabel, {
    text: "#",
    labelWrapperStyles: {
      flexShrink: '0'
    },
    children: /*#__PURE__*/_jsx(TextInput, Object.assign({
      maxLength: 7,
      onChange: handleInputChange,
      spellCheck: false,
      use: use,
      value: getDisplayColor(color),
      readOnly: readOnly
    }, inputProps))
  });
  const showOpacityInput = typeof opacity !== 'undefined' && showInput;
  const renderedOpacityInput = showOpacityInput ? /*#__PURE__*/_jsx(StyledFormControl, {
    "aria-label": opacityInputLabel || 'opacity input',
    label: opacityInputLabel,
    showOpacityInput: showOpacityInput,
    showInput: showInput,
    children: /*#__PURE__*/_jsx(StepperInput, Object.assign({
      disabled: disabled,
      value: opacity,
      onChange: handleOpacityInputChange,
      min: 0,
      max: 100,
      formatStyle: "percentage",
      readOnly: readOnly,
      use: use,
      baseValue: 0,
      currency: ""
    }, opacityInputProps))
  }) : null;
  const renderedColorPickerPanel = _renderColorPickerPanel(computedColorPicker, open, swatchColor, opacity, handleColorChange, handleOpenChange, _triggerEl.current);
  return /*#__PURE__*/_jsx(View, Object.assign({}, domProps, tokenProps, {
    display: "inline-block",
    children: /*#__PURE__*/_jsxs("div", {
      ref: _triggerEl,
      children: [/*#__PURE__*/_jsx(StyledFormControl, {
        "aria-label": colorInputLabel || 'color input',
        label: colorInputLabel,
        showInput: showInput,
        children: renderedHexInput
      }), renderedOpacityInput, /*#__PURE__*/_jsxs(SwatchWrapper, {
        $showInput: showInput,
        children: [/*#__PURE__*/_jsx(ColorSwatch, Object.assign({
          borderColor: "border-core-default",
          borderWidth: "100"
        }, swatchProps, {
          opacity: opacity,
          color: swatchColor,
          onClick: disabled || readOnly ? undefined : handleOpenChange
        })), /*#__PURE__*/_jsx(ColorPickerWrapper, {
          children: renderedColorPickerPanel
        })]
      })]
    })
  }));
};
export const ColorInput = Controllable(ColorInputComponent, ['color', 'open', 'opacity']);